/**
 * Chat Firebase Listeners
 * Centralized Firebase listener management with proper cleanup patterns
 */

import {
  listenToUserConversations,
  listenToConversationMessages,
  listenToConversationState,
  fetchUserConversationsMetadata,
  fetchConversationMessages,
  fetchConversationWithMessages,
} from '../../../services/conversationService';
import { Conversation, Message } from './chatTypes';

/**
 * Listener cleanup function type
 */
export type ListenerUnsubscribe = () => void;

/**
 * Callback types for different listeners
 */
export type ConversationsCallback = (conversations: Conversation[]) => void;
export type MessagesCallback = (messages: Message[]) => void;
export type ConversationStateCallback = (state: {
  status: 'busy' | 'idle';
  progress: string;
  type: 'reflection' | 'fraktal' | 'prompt-graph';
}) => void;

/**
 * Parameters for setting up conversations listener
 */
export interface ConversationsListenerParams {
  userId: string;
  callback: ConversationsCallback;
  fromTimestamp?: string;
}

/**
 * Parameters for setting up conversation messages listener
 */
export interface MessagesListenerParams {
  userId: string;
  conversationId: string;
  conversationType: 'reflection' | 'fraktal';
  callback: MessagesCallback;
}

/**
 * Parameters for setting up conversation state listener
 */
export interface StateListenerParams {
  userId: string;
  conversationId: string;
  conversationType: 'reflection' | 'fraktal' | 'prompt-graph';
  callback: ConversationStateCallback;
}

/**
 * Listener manager to track and cleanup active listeners
 */
class ChatListenerManager {
  private activeListeners: Map<string, ListenerUnsubscribe> = new Map();

  /**
   * Adds a listener to the manager with a unique key
   */
  addListener(key: string, unsubscribe: ListenerUnsubscribe): void {
    // Clean up existing listener with same key if it exists
    this.removeListener(key);
    this.activeListeners.set(key, unsubscribe);
  }

  /**
   * Removes a specific listener by key
   */
  removeListener(key: string): void {
    const unsubscribe = this.activeListeners.get(key);
    if (unsubscribe) {
      unsubscribe();
      this.activeListeners.delete(key);
    }
  }

  /**
   * Removes all active listeners
   */
  removeAllListeners(): void {
    this.activeListeners.forEach(unsubscribe => {
      unsubscribe();
    });
    this.activeListeners.clear();
  }

  /**
   * Gets the number of active listeners
   */
  getActiveListenerCount(): number {
    return this.activeListeners.size;
  }
}

// Global listener manager instance
export const chatListenerManager = new ChatListenerManager();

/**
 * Sets up a listener for user conversations with proper cleanup.
 * In local/emulator mode, uses polling as a workaround for Firebase emulator
 * onSnapshot bug with cross-collection writes (GitHub issue #2164).
 */
export const setupConversationsListener = (
  params: ConversationsListenerParams
): ListenerUnsubscribe => {
  const { userId, callback, fromTimestamp } = params;

  const wrappedCallback = (conversations: Conversation[]) => {
    // Sort conversations by timestamp (newest first) before calling callback
    const sortedConversations = [...conversations].sort(
      (a: Conversation, b: Conversation) =>
        new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime()
    );

    callback(sortedConversations);
  };

  // In local mode, use polling instead of onSnapshot due to emulator bug
  // where cross-collection writes from Cloud Functions don't trigger listeners
  const envValue = process.env.REACT_APP_ENVIRONMENT;
  const isLocalMode = envValue === 'local';
  console.log('[ChatListeners] Environment check:', {
    envValue,
    isLocalMode,
    useEmulators: process.env.REACT_APP_USE_EMULATORS,
  });

  if (isLocalMode) {
    console.log('[ChatListeners] Local mode detected - using polling instead of onSnapshot');
    const POLL_INTERVAL_MS = 2000; // Poll every 2 seconds
    let isActive = true;

    const poll = async () => {
      if (!isActive) return;

      try {
        const result = await fetchUserConversationsMetadata(userId, 50);
        if (isActive) {
          wrappedCallback(result.conversations);
        }
      } catch (error) {
        console.error('[ChatListeners] Polling error:', error);
      }

      if (isActive) {
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    // Start polling immediately
    poll();

    // Return cleanup function
    const unsubscribe = () => {
      console.log('[ChatListeners] Stopping local mode polling');
      isActive = false;
    };

    const listenerKey = `conversations-${userId}`;
    chatListenerManager.addListener(listenerKey, unsubscribe);

    return unsubscribe;
  }

  // Production mode: use real-time onSnapshot listeners
  const unsubscribe = listenToUserConversations(userId, wrappedCallback, fromTimestamp);

  // Register with manager for automatic cleanup
  const listenerKey = `conversations-${userId}`;
  chatListenerManager.addListener(listenerKey, unsubscribe);

  return unsubscribe;
};

/**
 * Sets up a listener for conversation messages with proper cleanup.
 * In local/emulator mode, uses polling as a workaround for Firebase emulator bug.
 */
export const setupMessagesListener = (params: MessagesListenerParams): ListenerUnsubscribe => {
  const { userId, conversationId, conversationType, callback } = params;

  // In local mode, use polling instead of onSnapshot due to emulator bug
  const isLocalMode = process.env.REACT_APP_ENVIRONMENT === 'local';

  if (isLocalMode) {
    console.log('[ChatListeners] Local mode - using polling for messages');
    const POLL_INTERVAL_MS = 1500; // Poll every 1.5 seconds for messages
    let isActive = true;

    const poll = async () => {
      if (!isActive) return;

      try {
        const messages = await fetchConversationMessages(
          userId,
          conversationId,
          conversationType as 'reflection' | 'fraktal' | 'prompt-graph'
        );
        if (isActive) {
          callback(messages);
        }
      } catch (error) {
        console.error('[ChatListeners] Messages polling error:', error);
      }

      if (isActive) {
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    const unsubscribe = () => {
      console.log('[ChatListeners] Stopping messages polling');
      isActive = false;
    };

    const listenerKey = `messages-${conversationId}`;
    chatListenerManager.addListener(listenerKey, unsubscribe);

    return unsubscribe;
  }

  // Production mode: use real-time onSnapshot listeners
  const unsubscribe = listenToConversationMessages(
    userId,
    conversationId,
    conversationType,
    callback
  );

  // Register with manager for automatic cleanup
  const listenerKey = `messages-${conversationId}`;
  chatListenerManager.addListener(listenerKey, unsubscribe);

  return unsubscribe;
};

/**
 * Sets up a listener for conversation state with proper cleanup.
 * In local/emulator mode, uses polling as a workaround for Firebase emulator bug.
 */
export const setupStateListener = (params: StateListenerParams): ListenerUnsubscribe => {
  const { userId, conversationId, conversationType, callback } = params;

  // In local mode, use polling instead of onSnapshot due to emulator bug
  const isLocalMode = process.env.REACT_APP_ENVIRONMENT === 'local';

  if (isLocalMode) {
    console.log('[ChatListeners] Local mode - using polling for conversation state');
    const POLL_INTERVAL_MS = 1000; // Poll every 1 second for state updates
    let isActive = true;

    const poll = async () => {
      if (!isActive) return;

      try {
        const conversation = await fetchConversationWithMessages(
          userId,
          conversationId,
          conversationType
        );
        if (isActive && conversation) {
          callback({
            status: conversation.loading ? 'busy' : 'idle',
            progress: conversation.progress || '',
            type: conversationType,
          });
        }
      } catch (error) {
        console.error('[ChatListeners] State polling error:', error);
      }

      if (isActive) {
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    const unsubscribe = () => {
      console.log('[ChatListeners] Stopping state polling');
      isActive = false;
    };

    const listenerKey = `state-${conversationId}`;
    chatListenerManager.addListener(listenerKey, unsubscribe);

    return unsubscribe;
  }

  // Production mode: use real-time onSnapshot listeners
  const unsubscribe = listenToConversationState(userId, conversationId, conversationType, callback);

  // Register with manager for automatic cleanup
  const listenerKey = `state-${conversationId}`;
  chatListenerManager.addListener(listenerKey, unsubscribe);

  return unsubscribe;
};

/**
 * Cleanup functions for specific listener types
 */
export const cleanupConversationsListener = (userId: string): void => {
  const listenerKey = `conversations-${userId}`;
  chatListenerManager.removeListener(listenerKey);
};

export const cleanupMessagesListener = (conversationId: string): void => {
  const listenerKey = `messages-${conversationId}`;
  chatListenerManager.removeListener(listenerKey);
};

export const cleanupStateListener = (conversationId: string): void => {
  const listenerKey = `state-${conversationId}`;
  chatListenerManager.removeListener(listenerKey);
};

/**
 * Cleanup all chat listeners - useful for app unmount or user logout
 */
export const cleanupAllChatListeners = (): void => {
  console.log(`Cleaning up ${chatListenerManager.getActiveListenerCount()} active chat listeners`);
  chatListenerManager.removeAllListeners();
};

/**
 * Get current listener statistics for debugging
 */
export const getChatListenerStats = () => {
  return {
    activeListeners: chatListenerManager.getActiveListenerCount(),
  };
};
